import React, { useEffect, useRef, useState } from "react"
import { POST, httpOk } from "helpers/api"

/**
 * AI-помощник оператора поддержки.
 *
 * От кабинетного отличается тем, ради чего и сделан: заведение выбирает оператор, а не
 * токен, и инструменты у него другие — трассировка остатка, расшифровка долга, журнал
 * действий, разбор чека, а ещё read-only запросы к базе и чтение кода. Заведение сервер
 * находит сам — по тексту, скриншоту или по ходу разбора; общий вопрос идёт без него.
 *
 * Текст по-русски без t(): админка не переводится.
 */

// Подсказки — половина ценности: оператор не обязан знать, какими словами спрашивать
const HINTS = [
	{
		title: "Остатки",
		items: [
			"Почему по мантам минус на складе?",
			"Куда делся товар за неделю?",
			"Какие позиции ушли в минус?",
		],
	},
	{
		title: "Деньги и долги",
		items: [
			"Почему у клиента такой долг?",
			"Клиент говорит, что оплатил — проверь историю",
			"Сколько заведение должно поставщикам?",
		],
	},
	{
		title: "Кто что сделал",
		items: [
			"Кто удалял чеки за последнюю неделю?",
			"Какие опасные действия были вчера?",
			"Кто давал ручные скидки?",
		],
	},
	{
		title: "Интеграции и касса",
		items: [
			"Почему товары не уходят в Яндекс?",
			"Бот не показывает меню — проверь",
			"Есть ли расхождения с кассой?",
		],
	},
]

function Index() {
	// Заведение сервер определяет по тексту (логин, id точки, id чека) и возвращает в ответе
	const [tenant, setTenant] = useState(null)

	const [messages, setMessages] = useState([])
	const [chatId, setChatId] = useState(null)
	const [question, setQuestion] = useState("")
	const [loading, setLoading] = useState(false)
	// Скриншоты из обращения: до двух, как в кабинетном помощнике
	const [images, setImages] = useState([])
	const [dragOver, setDragOver] = useState(false)

	const messagesEndRef = useRef(null)
	const fileInputRef = useRef(null)

	async function ask(text) {
		const value = (text ?? question).trim()
		// Подсказка-кнопка шлёт только свой текст — скриншоты к ней не цепляем
		const sending = text === undefined ? images : []
		if ((!value && sending.length === 0) || loading) return

		setMessages(prev => [...prev, { role: "user", content: value, images: sending }])
		setQuestion("")
		if (text === undefined) setImages([])
		setLoading(true)

		const response = await POST("/admin/ai-support", {
			question: value,
			images: sending,
			chat_id: chatId,
		}, { notification: false })

		setLoading(false)

		if (httpOk(response)) {
			setChatId(response.data.chat_id)
			// Заведение могло не определиться (общий вопрос) или смениться по ходу разбора
			setTenant(response.data.target_login
				? { login: response.data.target_login, poses_count: response.data.poses?.length ?? 0 }
				: null)
			setMessages(prev => [...prev, {
				role: "assistant",
				content: response.data.answer,
				tools: response.data.tools_used ?? [],
				duration: response.data.duration_ms,
			}])
		} else {
			setMessages(prev => [...prev, {
				role: "assistant",
				content: response?.data?.message ?? "Не удалось получить ответ",
				failed: true,
			}])
		}
	}

	// Скриншот сжимаем на клиенте: для разбора хватает 1600px по длинной стороне,
	// а мегабайты из буфера — это лишние токены и долгая отправка
	function addFiles(fileList) {
		const files = Array.from(fileList ?? []).filter(file => file.type.startsWith("image/"))
		files.slice(0, 2).forEach(file => {
			compressImage(file, (data) => {
				setImages(prev => [...prev, { mime: "image/jpeg", data: data }].slice(0, 2))
			})
		})
		return files.length > 0
	}

	function compressImage(file, callback) {
		const reader = new FileReader()
		reader.onload = (event) => {
			const image = new Image()
			image.onload = () => {
				const max = 1600
				let width = image.width
				let height = image.height
				if (width > max || height > max) {
					const ratio = Math.min(max / width, max / height)
					width = Math.round(width * ratio)
					height = Math.round(height * ratio)
				}

				const canvas = document.createElement("canvas")
				canvas.width = width
				canvas.height = height
				canvas.getContext("2d").drawImage(image, 0, 0, width, height)
				callback(canvas.toDataURL("image/jpeg", 0.8).split(",")[1])
			}
			image.src = event.target.result
		}
		reader.readAsDataURL(file)
	}

	function handlePaste(e) {
		if (addFiles(e.clipboardData?.files)) e.preventDefault()
	}

	function handleDrop(e) {
		e.preventDefault()
		setDragOver(false)
		if (!loading) addFiles(e.dataTransfer?.files)
	}

	function handleKeyDown(e) {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault()
			ask()
		}
	}

	useEffect(() => {
		if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" })
	}, [messages, loading])

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="d-flex align-items-center gap-2">
					<h4 className="vertical-center mb-0">AI-помощник поддержки</h4>
					{tenant &&
						<span className="badge bg-primary">
							{tenant.login} · {tenant.poses_count} точек
						</span>
					}
				</div>

				<div className="d-flex align-items-center gap-2">
					<button className="btn btn-primary"
						onClick={() => { setChatId(null); setMessages([]); setTenant(null) }}>
						Новый разбор
					</button>
				</div>
			</div>

			<div className="row" style={{ height: "calc(100vh - 120px)" }}>
				<div className="col-md-12 h-100">
					<div className="card h-100 mb-0">
						<div className="card-body d-flex flex-column" style={{ minHeight: 0 }}>

							<div className="flex-grow-1 mb-3" style={{ overflowY: "auto", overflowX: "hidden", minHeight: 0 }}>
								{messages.length === 0 && !loading &&
									<div>
										<div className="alert alert-info">
											Опишите задачу своими словами. Если она про конкретного клиента — укажите
											логин, id точки, id чека или название, либо приложите скриншот: помощник
											сам найдёт заведение. Общие вопросы («как считается себестоимость»)
											можно задавать без заведения — ответ будет по коду и базе.
										</div>

										<div className="alert alert-warning">
											Помощник работает только на чтение и опирается на данные системы.
											Он не видит, что происходит на стороне агрегаторов и не заменяет
											проверку документов.
										</div>

										<div className="row">
											{HINTS.map((group, index) => (
												<div className="col-md-6 mb-3" key={index}>
													<div className="border rounded p-3 h-100">
														<div className="fw-bold mb-2">{group.title}</div>
														{group.items.map((item, key) => (
															<button type="button"
																className="btn btn-outline-secondary btn-sm me-2 mb-2"
																key={key}
																onClick={() => setQuestion(item + " ")}>
																{item}
															</button>
														))}
													</div>
												</div>
											))}
										</div>
									</div>
								}

								{messages.map((item, index) => (
									<div className={`d-flex mb-3 ${item.role === "user" ? "justify-content-end" : ""}`} key={index}>
										<div className={`p-3 rounded ${item.role === "user" ? "bg-primary text-white" : "bg-light"}`}
											style={{ maxWidth: "80%", whiteSpace: "pre-wrap" }}>
											{item.images?.length > 0 &&
												<div className={`d-flex gap-2 ${item.content ? "mb-2" : ""}`}>
													{item.images.map((image, key) => (
														<img src={`data:${image.mime};base64,${image.data}`} alt="" key={key}
															style={{ maxHeight: "160px", maxWidth: "240px", borderRadius: "6px" }} />
													))}
												</div>
											}
											{item.content}

											{item.role === "assistant" && !item.failed && item.tools?.length > 0 &&
												<div className="text-muted mt-2" style={{ fontSize: "11px" }}>
													Проверено: {item.tools.join(", ")}
													{item.duration ? ` · ${Math.round(item.duration / 1000)} с` : ""}
												</div>
											}
										</div>
									</div>
								))}

								{loading &&
									<div className="d-flex mb-3">
										<div className="p-3 rounded bg-light text-muted">
											<i className="uil-spinner-alt"></i> Разбираю данные заведения...
										</div>
									</div>
								}

								<div ref={messagesEndRef}></div>
							</div>

							{images.length > 0 &&
								<div className="d-flex gap-2 mb-2">
									{images.map((item, index) => (
										<div className="position-relative" key={index}>
											<img src={`data:${item.mime};base64,${item.data}`} alt=""
												style={{ height: "64px", borderRadius: "6px", border: "1px solid #dee2e6" }} />
											<button type="button" className="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 px-1"
												title="Убрать"
												onClick={() => setImages(prev => prev.filter((_, key) => key !== index))}>
												<i className="uil-times"></i>
											</button>
										</div>
									))}
								</div>
							}

							{/* Скриншот: Ctrl+V в поле, скрепка или перетащить файл на поле */}
							<div className="d-flex gap-2"
								onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
								onDragLeave={() => setDragOver(false)}
								onDrop={handleDrop}>
								<input type="file" accept="image/*" multiple hidden ref={fileInputRef}
									onChange={(e) => { addFiles(e.target.files); e.target.value = "" }} />

								<button type="button" className="btn btn-outline-secondary"
									title="Приложить скриншот (до двух)"
									disabled={loading || images.length >= 2}
									onClick={() => fileInputRef.current?.click()}>
									<i className="uil-paperclip"></i>
								</button>

								<textarea className="form-control" rows="2"
									style={dragOver ? { borderColor: "#0d6efd", borderStyle: "dashed", background: "#f1f6ff" } : {}}
									placeholder={tenant
										? `Что разобрать по заведению ${tenant.login}? Скриншот — Ctrl+V или перетащите сюда`
										: "Вопрос или задача (логин / id точки / id чека — если про клиента). Скриншот — Ctrl+V или перетащите сюда"}
									value={question}
									disabled={loading}
									onPaste={handlePaste}
									onKeyDown={handleKeyDown}
									onChange={(e) => setQuestion(e.target.value)}>
								</textarea>

								<button className="btn btn-primary"
									disabled={loading || (!question.trim() && images.length === 0)}
									onClick={() => ask()}>
									<i className="uil-message"></i>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	)
}

export default Index
